import { useEffect,useMemo,useState } from 'react';
import { Link } from 'react-router-dom';
import type { AccountFacts,Freshness } from '@/lib/forex-display-api';

function formatMoney(value:string|null,currency:string|null){
 if(value===null||!/^[-+]?\d+(\.\d+)?$/.test(value))return '—';
 // Decimal string formatting avoids binary floating point and unsafe-integer loss.
 const [integer,fraction='']=value.split('.');
 return `${integer.replace(/\B(?=(\d{3})+(?!\d))/g,',')}.${fraction.padEnd(2,'0')} ${currency||'币种待确认'}`;
}
function useFreshness(freshness:Freshness){
 const origin=useMemo(()=>({monotonic:performance.now(),server: freshness.evaluated_at}),[freshness.evaluated_at]);
 const [,setTick]=useState(0);
 useEffect(()=>{const timer=setInterval(()=>setTick(v=>v+1),1000);return()=>clearInterval(timer);},[]);
 const now=origin.server+(performance.now()-origin.monotonic)/1000;
 const age=freshness.observed_at===null?null:Math.floor(now-freshness.observed_at);
 return {age,stale:freshness.state==='stale'||age!==null&&age>freshness.stale_after_seconds};
}
export function MoneyCell({value,currency,freshness}:{value:string|null;currency:string|null;freshness:Freshness}){
 const {stale}=useFreshness(freshness);
 return <span className="tabular-nums whitespace-nowrap">{formatMoney(value,currency)}{value!==null&&stale&&<small className="block text-amber-600">数据已过期</small>}</span>;
}
export function LeverageCell({facts}:{facts:AccountFacts}){
 const {stale}=useFreshness(facts.snapshot.freshness);
 return <span>{facts.snapshot.leverage?`1:${facts.snapshot.leverage}`:'—'}{facts.snapshot.leverage&&stale?<small className="block text-amber-600">数据已过期</small>:null}</span>;
}
export function ConnectionCell({facts}:{facts:AccountFacts}){
 const {age,stale}=useFreshness(facts.connection.freshness);
 const at=facts.connection.last_seen_at;
 const online=facts.connection.state==='online'&&!stale;
 const label=online?'在线':facts.connection.state==='not_provisioned'?'未开通':stale?'心跳已超时':facts.connection.state==='offline'?'离线':'待确认';
 return <span className={online?'text-emerald-600':'text-text-muted'}>{label}<small className="block" title={at?new Date(at*1000).toLocaleString('zh-CN',{timeZone:'Asia/Shanghai'}):undefined}>{age!==null&&age>=0?`${age} 秒前`:'暂无上报'}</small></span>;
}
export function InstanceLink({facts}:{facts:AccountFacts}){
 const i=facts.instance;
 return i?<Link className="text-brand" to={`/forex/terminal/detail?id=${encodeURIComponent(i.terminal_id)}&job_id=${encodeURIComponent(i.job_id)}`}>查看实例</Link>:<span>{facts.identity_status==='conflict'?'实例关联冲突':'—'}</span>;
}
export function AccountExecutionCell({facts}:{facts:AccountFacts}){
 const state=facts.account_execution_readiness?.state;
 const label=state==='ready'?'可进入逐笔风控':state==='exit_only'?'只允许退出':state==='not_ready'?'暂不可开仓':'执行状态待核对';
 return <span>{label}<small className="block text-text-muted">{facts.account_environment==='real'?'真实账户':facts.account_environment==='simulation'?'模拟账户':'账户环境待核对'}</small></span>;
}
export function AccountSnapshotPanel({facts}:{facts:AccountFacts}){
 return <section aria-label="账户运行事实" className="rounded-xl bg-white p-5 grid gap-5 sm:grid-cols-4 text-sm">
 <div><p className="text-text-muted mb-2">余额</p><MoneyCell value={facts.snapshot.balance} currency={facts.snapshot.currency} freshness={facts.snapshot.freshness}/></div>
 <div><p className="text-text-muted mb-2">净值</p><MoneyCell value={facts.snapshot.equity} currency={facts.snapshot.currency} freshness={facts.snapshot.freshness}/></div>
 <div><p className="text-text-muted mb-2">杠杆</p><LeverageCell facts={facts}/></div>
 <div><p className="text-text-muted mb-2">最近上报</p><ConnectionCell facts={facts}/></div>
 <div><p className="text-text-muted mb-2">最近风控检查</p>暂无检查结果</div><InstanceLink facts={facts}/>
 <div><p className="text-text-muted mb-2">账户执行状态</p><AccountExecutionCell facts={facts}/></div>
 </section>;
}
